/**
 * Building a message, which this package has never been able to do.
 *
 * `mime/` parses: `structure.ts` reads a BODYSTRUCTURE, `entities.ts` and
 * `html.ts` turn markup into text, `rfc2047.ts` DECODES encoded words. Nothing
 * anywhere encodes, which is why "compose and send" is described as the largest
 * single piece left in the product rather than as a UI job — the client is the
 * visible half and this is the half that decides whether what arrives is a
 * message or a mess.
 *
 * ## The rules that actually bite
 *
 * RFC 5322 §2.1.1 puts a HARD limit of 998 octets on a line, and a SHOULD at
 * 78. Exceeding the hard one is not a style problem: Postfix will accept it,
 * some intermediate will fold it somewhere arbitrary, and a signature computed
 * over the original no longer verifies. Every producer here respects it —
 * headers by folding at a syntactic point, bodies by choosing an encoding that
 * cannot produce a long line.
 *
 * RFC 2047 encoded words are for HEADERS ONLY, may not exceed 75 characters
 * INCLUDING the `=?utf-8?B?...?=` wrapper, and MUST NOT split a multi-byte
 * character across two of them. That last one is the trap: a naive
 * `chunk(base64(utf8(text)), 60)` splits mid-character and produces a name with
 * a replacement glyph in the middle, on some clients and not others.
 *
 * A BARE `\n` IN A MESSAGE IS A BUG that only shows up in transit. SMTP is
 * CRLF-terminated and dot-stuffed; a lone LF reaching the wire is what turns a
 * message into two, or truncates one. Everything here emits CRLF and
 * `normaliseNewlines` is applied to every caller-supplied string.
 */
import { parseAddressList, type ParsedAddress } from '../addresses/parse.ts'
import { normalizeMessageId } from '../threading/index.ts'

/** RFC 5322 §2.1.1: no line may exceed 998 octets, excluding the CRLF. */
export const MAX_LINE_OCTETS = 998
/** The SHOULD in the same section, and what every producer here aims at. */
export const WRAP_AT = 76

export interface OutgoingAddress {
  readonly address: string
  readonly name?: string
}

export interface OutgoingAttachment {
  readonly filename: string
  /** Lowercased `type/subtype`. Defaults to application/octet-stream. */
  readonly type?: string
  readonly content: Uint8Array
  /** Render inside the body — an image a `cid:` URL points at. */
  readonly inline?: boolean
  /** Content-ID without angle brackets, for an inline part. */
  readonly contentId?: string
}

export interface OutgoingMessage {
  readonly from: OutgoingAddress
  readonly to?: readonly OutgoingAddress[]
  readonly cc?: readonly OutgoingAddress[]
  readonly bcc?: readonly OutgoingAddress[]
  readonly replyTo?: readonly OutgoingAddress[]
  readonly subject?: string
  readonly text?: string
  readonly html?: string
  readonly attachments?: readonly OutgoingAttachment[]
  /** Seconds. Injected so a built message is reproducible in a test. */
  readonly date: number
  /** Without angle brackets; `buildMessage` adds them. */
  readonly messageId: string
  /** The message being replied to, without angle brackets. */
  readonly inReplyTo?: string
  /**
   * The full ancestry, oldest first, without angle brackets.
   *
   * RFC 5322 §3.6.4 and the threading this repo already implements both depend
   * on the ORDER, so it is a list and not a set: `packages/core/threading`
   * reduces a reference set in `References` order, and a defect in exactly that
   * fragmented 45% of conversations before Phase 0.
   */
  readonly references?: readonly string[]
  /** Extra headers, e.g. `X-Mailer`. Names are used as given. */
  readonly headers?: Readonly<Record<string, string>>
}

const CRLF = '\r\n'

/** Every newline becomes CRLF, whatever the caller had. */
export const normaliseNewlines = (text: string): string => text.replace(/\r\n|\r|\n/g, CRLF)

const utf8 = new TextEncoder()

const isAscii = (text: string): boolean => !/[^\x20-\x7e\t]/.test(text)

/**
 * One RFC 2047 encoded word, or several, never splitting a character.
 *
 * BASE64 AND NOT Q-ENCODING. Q looks friendlier for a mostly-ASCII string and
 * is worse everywhere it matters: its escaping rules differ between a phrase and
 * a comment, `_` means space only in some positions, and getting it wrong is
 * silent. Base64 has one rule.
 *
 * THE CHUNKING IS OVER CODE POINTS, NOT BYTES, which is the whole subtlety.
 * A word may not exceed 75 characters including `=?utf-8?B?` and `?=`, and it
 * MUST NOT split a multi-byte character — so this grows a chunk one code point
 * at a time and stops before the encoded form would overrun.
 */
export function encodeWord(text: string): string {
  if (isAscii(text)) return text

  const prefix = '=?utf-8?B?'
  const suffix = '?='
  const budget = 75 - prefix.length - suffix.length
  const words: string[] = []

  let chunk = ''
  const flush = (): void => {
    if (chunk !== '') words.push(prefix + base64(utf8.encode(chunk)) + suffix)
    chunk = ''
  }

  // `[...text]` iterates CODE POINTS, so an astral character (an emoji) is one
  // step and cannot be halved.
  for (const character of text) {
    const candidate = chunk + character
    if (base64(utf8.encode(candidate)).length > budget) flush()
    chunk += character
  }
  flush()
  // Encoded words in a sequence are joined by whitespace, which a decoder
  // removes between two adjacent ones (RFC 2047 §6.2).
  return words.join(' ')
}

/** Base64 without line breaks. */
export function base64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

/** Base64 in 76-character lines, which is what a body part needs. */
export function base64Lines(bytes: Uint8Array): string {
  const encoded = base64(bytes)
  const lines: string[] = []
  for (let i = 0; i < encoded.length; i += WRAP_AT) lines.push(encoded.slice(i, i + WRAP_AT))
  return lines.join(CRLF)
}

/**
 * One address, as a header value.
 *
 * A DISPLAY NAME IS EITHER AN ENCODED WORD OR A QUOTED STRING, NEVER BOTH.
 * RFC 2047 §5 is explicit that an encoded word may not appear inside a quoted
 * string — a decoder that sees `"=?utf-8?B?...?="` is required to leave it
 * alone, so the reader gets the raw gibberish. Non-ASCII therefore goes through
 * `encodeWord` UNQUOTED, and an ASCII name containing a special goes through
 * quoting instead.
 */
export function formatAddress(address: OutgoingAddress): string {
  const name = address.name?.trim()
  if (!name) return address.address
  if (!isAscii(name)) return `${encodeWord(name)} <${address.address}>`
  // RFC 5322 §3.2.3 specials, which force a quoted-string.
  if (/[()<>@,;:\\".[\]]/.test(name)) {
    return `"${name.replace(/([\\"])/g, '\\$1')}" <${address.address}>`
  }
  return `${name} <${address.address}>`
}

const formatAddressList = (list: readonly OutgoingAddress[]): string =>
  list.map(formatAddress).join(', ')

/**
 * Fold a header to stay under the line limit.
 *
 * AT A SPACE, AND ONLY AT A SPACE. RFC 5322 folding inserts CRLF before
 * existing whitespace; inventing a break anywhere else changes the value. A
 * token with no space in it that is longer than the limit is left long — that
 * is a caller's problem (a 900-character URL in a header), and silently
 * mangling it would be worse than a long line.
 */
export function foldHeader(name: string, value: string): string {
  const line = `${name}: ${value}`
  if (line.length <= WRAP_AT) return line

  const words = line.split(' ')
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    if (current === '') current = word
    else if (`${current} ${word}`.length <= WRAP_AT) current += ` ${word}`
    else if (current === `${name}:`) {
      // A FOLD AFTER THE NAME ALONE HELPS NOTHING. The continuation is still
      // one over-long token, and the message has gained a line break for no
      // reason — which is worse than the long line, because a header that
      // begins with an empty value reads as a different header.
      current += ` ${word}`
    } else {
      lines.push(current)
      current = word
    }
  }
  if (current !== '') lines.push(current)
  // A continuation line begins with whitespace, which is what makes it one.
  return lines.join(`${CRLF} `)
}

/**
 * Quoted-printable, for a text part that is not pure ASCII.
 *
 * WHY NOT ALWAYS BASE64: a quoted-printable body is readable in a raw message,
 * which matters for a client whose whole design has a "show the protocol"
 * surface. Why not always quoted-printable: it is 3x the size for text that is
 * mostly non-ASCII, so the builder picks per part.
 *
 * THE TRAILING-WHITESPACE RULE IS NOT OPTIONAL. RFC 2045 §6.7 requires a space
 * or tab at the end of a line to be encoded, because an intermediate is allowed
 * to strip it — and a stripped trailing space silently changes a signature.
 */
export function quotedPrintable(text: string): string {
  const encoded: string[] = []
  for (const line of normaliseNewlines(text).split(CRLF)) {
    let out = ''
    const bytes = utf8.encode(line)
    for (const [index, byte] of bytes.entries()) {
      const isLast = index === bytes.length - 1
      const printable = byte >= 0x20 && byte <= 0x7e && byte !== 0x3d
      if (printable && !((byte === 0x20 || byte === 0x09) && isLast)) {
        out += String.fromCharCode(byte)
      } else {
        out += `=${byte.toString(16).toUpperCase().padStart(2, '0')}`
      }
    }
    // Soft line breaks keep every line under the limit. The `=` at the end is
    // what tells a decoder the line continues.
    const wrapped: string[] = []
    while (out.length > WRAP_AT - 1) {
      // Never break an `=XX` escape across the fold.
      let cut = WRAP_AT - 1
      if (out[cut - 1] === '=') cut -= 1
      else if (out[cut - 2] === '=') cut -= 2
      wrapped.push(out.slice(0, cut) + '=')
      out = out.slice(cut)
    }
    wrapped.push(out)
    encoded.push(wrapped.join(CRLF))
  }
  return encoded.join(CRLF)
}

/**
 * A boundary that cannot appear in the content it delimits.
 *
 * The randomness is the caller's — `buildMessage` takes it — because a builder
 * that reached for `Math.random` could not be tested, and a message that
 * differs between two runs cannot be compared byte for byte.
 */
export const boundaryFor = (seed: string, index: number): string => `--=_hearth_${seed}_${index}`

interface Part {
  readonly headers: readonly (readonly [string, string])[]
  readonly body: string
}

function textPart(content: string, subtype: 'plain' | 'html'): Part {
  const normalised = normaliseNewlines(content)
  const ascii = isAscii(normalised.replace(/\r\n/g, ''))
  // A LONG LINE FORCES AN ENCODING EVEN WHEN THE TEXT IS ASCII. An HTML body is
  // routinely one enormous line, and 7bit with a 2,000-character line is a
  // message that violates the hard limit.
  const longLine = normalised.split(CRLF).some((line) => utf8.encode(line).length > MAX_LINE_OCTETS)
  if (ascii && !longLine) {
    return {
      headers: [
        ['Content-Type', `text/${subtype}; charset=utf-8`],
        ['Content-Transfer-Encoding', '7bit'],
      ],
      body: normalised,
    }
  }
  return {
    headers: [
      ['Content-Type', `text/${subtype}; charset=utf-8`],
      ['Content-Transfer-Encoding', 'quoted-printable'],
    ],
    body: quotedPrintable(normalised),
  }
}

function attachmentPart(attachment: OutgoingAttachment): Part {
  const type = attachment.type ?? 'application/octet-stream'
  const disposition = attachment.inline ? 'inline' : 'attachment'
  const name = filenameParams(attachment.filename)
  const headers: (readonly [string, string])[] = [
    // NO `name*=` BESIDE `name=`. The `name` parameter of Content-Type is a
    // leftover from RFC 1341 that RFC 2045 never defined; `filename` on
    // Content-Disposition is where RFC 2231 applies and where every client
    // looks first. Emitting an extended form for a parameter with no
    // specification is inventing syntax, and the ASCII form here is the
    // fallback that legacy readers were already reading.
    ['Content-Type', `${type}; name="${name.quoted}"`],
    ['Content-Transfer-Encoding', 'base64'],
    [
      'Content-Disposition',
      `${disposition}; filename="${name.quoted}"` + (name.extended === null ? '' : `; ${name.extended}`),
    ],
  ]
  if (attachment.contentId) headers.push(['Content-ID', `<${attachment.contentId}>`])
  return { headers, body: base64Lines(attachment.content) }
}

/** RFC 5987 attr-char: what may appear unencoded in an extended parameter. */
const ATTR_CHAR = /[A-Za-z0-9!#$&+\-.^_`|~]/

/**
 * One string as RFC 5987 percent-encoded UTF-8.
 *
 * OVER BYTES AND NOT OVER CHARACTERS, which is the whole of it: `encodeURI`
 * and friends leave `'`, `!`, `(`, `)` and `*` alone, and every one of them is
 * a `tspecial` that ends a parameter early in the reader that matters. So the
 * allowlist is attr-char and the escape is applied to everything else,
 * including the space and the `%` itself.
 */
const percentEncode = (value: string): string => {
  let out = ''
  for (const byte of utf8.encode(value)) {
    const character = String.fromCharCode(byte)
    out += ATTR_CHAR.test(character) ? character : `%${byte.toString(16).toUpperCase().padStart(2, '0')}`
  }
  return out
}

/**
 * A filename as header parameters, in BOTH forms.
 *
 * BOTH, NEVER ONE. `filename*=utf-8''…` (RFC 2231, profiled by RFC 5987) is the
 * only way to spell a non-ASCII name in a header, and a reader old enough not to
 * understand it must still be handed something — so the quoted `filename=` stays
 * and carries an ASCII rendering. RFC 6266 §4.3 requires a reader that
 * understands both to prefer the extended one, so nobody sees the fallback who
 * can read the real name.
 *
 * THE FALLBACK IS NOT A TRANSLITERATION. Folding `ü` to `u` needs a table per
 * language and gets Turkish wrong; `_` is honest about having lost something and
 * keeps the extension, which is the part a save dialog and an operating system
 * both act on.
 *
 * THE EXTENDED FORM APPEARS ONLY WHEN THE NAME NEEDS IT, so every message with
 * an ASCII filename is byte for byte what this builder emitted before this
 * existed. That matters more here than it looks: the outbox stores the message
 * BUILT, the copy in Sent is APPENDed from those same bytes, and Postfix signs
 * them afterwards — so a gratuitous change lands in three places at once.
 *
 * AN ABSURDLY LONG NAME LOSES THE EXTENDED FORM RATHER THAN BEING CUT SHORT.
 * `filename*=utf-8''…` is one token with no space in it and `foldHeader` folds
 * at spaces only, so a CJK name — nine characters per character once encoded —
 * can push a line past the 998-octet hard limit this file's header calls
 * non-negotiable. Truncating would produce a name that is WRONG while looking
 * right, and can halve a `%XX`; dropping it leaves the fallback, which already
 * looks approximate. RFC 2231 §3 continuations are the other answer and are not
 * worth their failure modes for a filename no filesystem would accept anyway.
 */
export function filenameParams(filename: string): { quoted: string; extended: string | null } {
  // A CR or LF would end the header and make the rest of the name into header
  // syntax. Everything else outside printable ASCII becomes `_`.
  const ascii = filename.replace(/[\r\n]/g, '').replace(/[^\x20-\x7e]/g, '_')
  // A quote or a backslash ends the parameter early and produces an unparseable
  // header — the one case the quoted form has always had to escape.
  const quoted = ascii.replace(/[\\"]/g, '\\$&')
  if (ascii === filename) return { quoted, extended: null }

  const extended = `filename*=utf-8''${percentEncode(filename)}`
  // One leading space, because a folded continuation line begins with one and
  // an unbreakable token is alone on its line.
  return { quoted, extended: 1 + extended.length <= MAX_LINE_OCTETS ? extended : null }
}

const renderPart = (part: Part): string =>
  part.headers.map(([name, value]) => foldHeader(name, value)).join(CRLF) + CRLF + CRLF + part.body

/**
 * Build one RFC 5322 message, ready for SMTP DATA or IMAP APPEND.
 *
 * THE STRUCTURE FOLLOWS THE CONTENT, and the shapes are the four every mail
 * client produces:
 *
 *   text only                    -> text/plain
 *   text + html                  -> multipart/alternative
 *   text + attachments           -> multipart/mixed
 *   text + html + attachments    -> multipart/mixed [ alternative, files… ]
 *
 * The alternative goes INSIDE the mixed and not beside it, because RFC 2046
 * §5.1.4 says a reader picks the last part of an alternative it understands —
 * so a client that showed the HTML would otherwise have to choose between the
 * body and the attachments rather than showing both.
 */
export function buildMessage(message: OutgoingMessage, seed = 'x'): string {
  const headers: (readonly [string, string])[] = []

  headers.push(['Date', rfc5322Date(message.date)])
  headers.push(['Message-ID', `<${message.messageId}>`])
  headers.push(['From', formatAddress(message.from)])
  if (message.to?.length) headers.push(['To', formatAddressList(message.to)])
  if (message.cc?.length) headers.push(['Cc', formatAddressList(message.cc)])
  if (message.replyTo?.length) headers.push(['Reply-To', formatAddressList(message.replyTo)])
  // NO `Bcc` HEADER. The recipients go in the SMTP envelope and nowhere else;
  // a Bcc header in the transmitted message is the classic way to tell every
  // recipient who was blind-copied.
  headers.push(['Subject', encodeWord(message.subject ?? '')])
  // NORMALISED BEFORE IT IS WRAPPED, so this cannot emit `<<abc@host>>`. The
  // caller is a composer holding a row from the index, and what the index holds
  // is `normalizeMessageId`'s output — but "the caller already stripped the
  // brackets" is an assumption about somebody else's code, which is the shape
  // this repo keeps being bitten by. It was bitten by exactly this one: the
  // column used to keep the brackets, `Compose.tsx` passed the stored value
  // straight through, and every reply went out with a doubly-bracketed
  // In-Reply-To that no threading algorithm anywhere would match.
  if (message.inReplyTo) headers.push(['In-Reply-To', `<${normalizeMessageId(message.inReplyTo)}>`])
  if (message.references?.length) {
    headers.push(['References', message.references.map((id) => `<${normalizeMessageId(id)}>`).join(' ')])
  }
  for (const [name, value] of Object.entries(message.headers ?? {})) {
    headers.push([name, isAscii(value) ? value : encodeWord(value)])
  }
  headers.push(['MIME-Version', '1.0'])

  const files = message.attachments ?? []
  const text = message.text === undefined ? undefined : textPart(message.text, 'plain')
  const html = message.html === undefined ? undefined : textPart(message.html, 'html')

  // A message with no body at all is legal and is what a "no text" reply is.
  const bodyParts = [text, html].filter((part): part is Part => part !== undefined)
  let root: Part
  if (bodyParts.length === 0 && files.length === 0) {
    root = textPart('', 'plain')
  } else if (files.length === 0 && bodyParts.length === 1) {
    root = bodyParts[0]!
  } else if (files.length === 0) {
    root = multipart('alternative', bodyParts, boundaryFor(seed, 1))
  } else {
    const body =
      bodyParts.length === 0
        ? textPart('', 'plain')
        : bodyParts.length === 1
          ? bodyParts[0]!
          : multipart('alternative', bodyParts, boundaryFor(seed, 1))
    root = multipart('mixed', [body, ...files.map(attachmentPart)], boundaryFor(seed, 0))
  }

  const all = [...headers, ...root.headers]
  return all.map(([name, value]) => foldHeader(name, value)).join(CRLF) + CRLF + CRLF + root.body + CRLF
}

function multipart(subtype: string, parts: readonly Part[], boundary: string): Part {
  const body =
    parts.map((part) => `--${boundary}${CRLF}${renderPart(part)}`).join(CRLF) +
    CRLF +
    `--${boundary}--`
  return {
    headers: [['Content-Type', `multipart/${subtype}; boundary="${boundary}"`]],
    body,
  }
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * `Date:` in RFC 5322 form, in UTC.
 *
 * UTC AND NOT LOCAL TIME, which is a deliberate loss. A local offset tells
 * every recipient roughly where the sender is, and the value of that to a reader
 * is nil — the client renders in their own zone either way.
 */
export function rfc5322Date(seconds: number): string {
  const at = new Date(seconds * 1000)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return (
    `${DAYS[at.getUTCDay()]}, ${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]} ${at.getUTCFullYear()} ` +
    `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}:${pad(at.getUTCSeconds())} +0000`
  )
}

/**
 * Every address a message must actually be delivered to.
 *
 * THE ENVELOPE IS NOT THE HEADERS, and this is the function that keeps them
 * apart: Bcc recipients appear here and in no header, which is the entire
 * mechanism by which a blind copy is blind.
 */
export function envelopeRecipients(message: OutgoingMessage): string[] {
  const all = [...(message.to ?? []), ...(message.cc ?? []), ...(message.bcc ?? [])]
  const seen = new Set<string>()
  const out: string[] = []
  for (const address of all) {
    const key = address.address.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(address.address)
  }
  return out
}

/** Parse a typed-in recipient list into addresses this builder can format. */
export function parseRecipients(raw: string): OutgoingAddress[] {
  return parseAddressList(raw).map((parsed: ParsedAddress) => ({
    address: parsed.address,
    ...(parsed.name ? { name: parsed.name } : {}),
  }))
}
